import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { FaShopify, FaCheck, FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { SiMeta } from 'react-icons/si';
import { HiUser, HiDatabase, HiClipboardCheck } from 'react-icons/hi';
import PhoneNumberInput from '~/components/app/components/PhoneNumberInput';
import MetaAdsConnectorModal from '~/components/app/components/MetaAdsConnectorModal';
import { useAuth } from '~/contexts/AuthContext';
import { getRequestWithAuth, patchRequestWithAuth } from '~/lib/api';
import {
  saveWizardOAuthState,
  clearWizardOAuthState,
} from '~/lib/wizard-oauth-state';

// Store categories for dropdown
const STORE_CATEGORIES = [
  'Apparel & Fashion',
  'Beauty & Personal Care',
  'Electronics & Gadgets',
  'Food & Beverages',
  'Health & Wellness',
  'Home & Living',
  'Jewelry & Accessories',
  'Kids & Baby',
  'Pet Supplies',
  'Sports & Fitness',
  'Other',
];

type WizardStep = 'user-info' | 'connectors' | 'review';

type UserInfoData = {
  firstName: string;
  lastName: string;
  storeCategory: string;
  phone: string;
  whatsappConsent: boolean;
};

type ConnectionData = {
  _id: string;
  name: string;
  connection_type: string;
  sync_status?: string | null;
};

type SetupWizardProps = {
  onComplete: (connectionId: string) => Promise<void>;
  existingShopifyConnection?: ConnectionData;
  existingMetaConnection?: ConnectionData;
};

export default function SetupWizard({
  onComplete,
  existingShopifyConnection,
  existingMetaConnection,
}: SetupWizardProps) {
  const { user, refreshAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine initial step based on existing user data and connections
  const getInitialStep = (): WizardStep => {
    const hasCompleteProfile =
      user?.first_name &&
      user?.last_name &&
      user?.store_category &&
      user?.phone_number;

    if (!hasCompleteProfile) return 'user-info';
    if (!existingShopifyConnection) return 'connectors';
    return 'review';
  };

  const [currentStep, setCurrentStep] = useState<WizardStep>(getInitialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(!!existingShopifyConnection);
  const [shopifyConnectionId, setShopifyConnectionId] = useState<string | null>(
    existingShopifyConnection?._id ?? null
  );
  const [error, setError] = useState<string | null>(null);

  // User info state
  const [userInfo, setUserInfo] = useState<UserInfoData>({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    storeCategory: user?.store_category || '',
    phone: user?.phone_number || '',
    whatsappConsent: user?.whatsapp_consent || false,
  });
  const [phoneError, setPhoneError] = useState('');

  // Shopify connection state
  const [shopifyError, setShopifyError] = useState<string | null>(null);
  const [isConnectingShopify, setIsConnectingShopify] = useState(false);

  // Meta Ads state
  const [metaConnected, setMetaConnected] = useState(!!existingMetaConnection);
  const [metaConnectionId, setMetaConnectionId] = useState<string | null>(
    existingMetaConnection?._id ?? null
  );
  const [showMetaModal, setShowMetaModal] = useState(false);

  // Pre-fill user info if available
  useEffect(() => {
    if (user) {
      setUserInfo((prev) => ({
        ...prev,
        firstName: user.first_name || prev.firstName,
        lastName: user.last_name || prev.lastName,
        storeCategory: user.store_category || prev.storeCategory,
        phone: user.phone_number || prev.phone,
        whatsappConsent: user.whatsapp_consent || prev.whatsappConsent,
      }));
    }
  }, [user]);

  // Detect return from Shopify or Meta OAuth
  useEffect(() => {
    const shopifyOAuthReturn = searchParams.get('shopifyOAuthReturn');
    const shopifyConnectionIdParam = searchParams.get('shopifyConnectionId');
    const metaOAuthReturn = searchParams.get('metaOAuthReturn');
    const metaConnectionIdParam = searchParams.get('metaConnectionId');

    if (shopifyOAuthReturn === 'success' && shopifyConnectionIdParam) {
      // Clear URL params
      setSearchParams({}, { replace: true });
      // Clear stored wizard state
      clearWizardOAuthState();
      // Update wizard state with successful connection
      setShopifyConnected(true);
      setShopifyConnectionId(shopifyConnectionIdParam);
      // Ensure we're on the connectors step
      setCurrentStep('connectors');
    } else if (metaOAuthReturn === 'success' && metaConnectionIdParam) {
      // Clear URL params
      setSearchParams({}, { replace: true });
      // Clear stored wizard state
      clearWizardOAuthState();
      // Update wizard state with successful connection
      setMetaConnected(true);
      setMetaConnectionId(metaConnectionIdParam);
      // Ensure we're on the connectors step
      setCurrentStep('connectors');
    } else if (shopifyOAuthReturn === 'error') {
      // Clear URL params and show error message
      setSearchParams({}, { replace: true });
      clearWizardOAuthState();
      setShopifyError('Shopify connection was cancelled or failed. Please try again.');
      setCurrentStep('connectors');
    } else if (metaOAuthReturn === 'error') {
      // Clear URL params (Meta error - user can retry via modal)
      setSearchParams({}, { replace: true });
      clearWizardOAuthState();
      setCurrentStep('connectors');
    }
  }, [searchParams, setSearchParams]);

  // User update mutation
  const updateUserMutation = useMutation({
    mutationFn: async (data: {
      first_name: string;
      last_name: string;
      store_category: string;
      phone_number: string;
      whatsapp_consent: boolean;
      signup_source?: string;
    }) => {
      return patchRequestWithAuth('/api/v1/users/me', data);
    },
    onSuccess: () => {
      refreshAuth();
    },
  });

  const validatePhone = (phone: string): boolean => {
    const pattern = /^\+[1-9]\d{7,14}$/;
    return pattern.test(phone);
  };

  const isUserInfoValid = (): boolean => {
    return (
      userInfo.firstName.trim().length > 0 &&
      userInfo.lastName.trim().length > 0 &&
      userInfo.storeCategory.length > 0 &&
      validatePhone(userInfo.phone)
    );
  };

  const handleNextFromUserInfo = async () => {
    if (!isUserInfoValid()) {
      if (!validatePhone(userInfo.phone)) {
        setPhoneError('Please enter a valid phone number');
      }
      return;
    }

    setIsSubmitting(true);
    try {
      await updateUserMutation.mutateAsync({
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        store_category: userInfo.storeCategory,
        phone_number: userInfo.phone,
        whatsapp_consent: userInfo.whatsappConsent,
        signup_source: 'shopify',
      });
      setCurrentStep('connectors');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save user info');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shopify OAuth flow
  const handleShopifyOAuth = async () => {
    setIsConnectingShopify(true);
    setShopifyError(null);
    try {
      // Save wizard state before redirect
      saveWizardOAuthState({
        returnUrl: '/app/dashboard',
        shopifyConnected: false,
        shopifyConnectionId: null,
        metaConnected,
        metaConnectionId,
        oauthType: 'shopify',
      });
      // Get OAuth URL and redirect
      const { installUrl } = await getRequestWithAuth('/api/v1/shopify/installUrl');
      window.location.href = installUrl;
    } catch (err: any) {
      setShopifyError(err.response?.data?.detail || 'Failed to start Shopify connection');
      setIsConnectingShopify(false);
    }
  };

  // Save wizard state before Meta OAuth redirect
  const handleMetaOAuthStart = () => {
    saveWizardOAuthState({
      returnUrl: '/app/dashboard',
      shopifyConnected,
      shopifyConnectionId,
      metaConnected: false,
      metaConnectionId: null,
      oauthType: 'meta',
    });
    setShowMetaModal(true);
  };

  const handleGenerateReport = async () => {
    if (!shopifyConnectionId) {
      setError('Missing Shopify connection. Please reconnect your store.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onComplete(shopifyConnectionId);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 'user-info', label: 'Your Info', icon: HiUser },
    { id: 'connectors', label: 'Connect Data', icon: HiDatabase },
    { id: 'review', label: 'Generate', icon: HiClipboardCheck },
  ];

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] py-8">
      <div className="card w-full max-w-2xl bg-base-200 shadow-xl">
        <div className="card-body gap-6">
          {/* Step indicator */}
          <ul className="steps steps-horizontal w-full mb-4">
            {steps.map((step, index) => (
              <li
                key={step.id}
                className={`step ${index <= currentStepIndex ? 'step-primary' : ''}`}
              >
                <span className="hidden sm:inline">{step.label}</span>
              </li>
            ))}
          </ul>

          {/* Step 1: User Info */}
          {currentStep === 'user-info' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <HiUser className="text-primary" />
                Tell us about yourself
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text">First Name *</span>
                  </label>
                  <input
                    type="text"
                    placeholder="John"
                    value={userInfo.firstName}
                    onChange={(e) =>
                      setUserInfo((prev) => ({ ...prev, firstName: e.target.value }))
                    }
                    className="input input-bordered"
                  />
                </div>

                <div className="form-control">
                  <label className="label py-1">
                    <span className="label-text">Last Name *</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={userInfo.lastName}
                    onChange={(e) =>
                      setUserInfo((prev) => ({ ...prev, lastName: e.target.value }))
                    }
                    className="input input-bordered"
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="label py-1">
                  <span className="label-text">Store Category *</span>
                </label>
                <select
                  value={userInfo.storeCategory}
                  onChange={(e) =>
                    setUserInfo((prev) => ({ ...prev, storeCategory: e.target.value }))
                  }
                  className="select select-bordered w-full"
                >
                  <option value="">Select a category</option>
                  {STORE_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <PhoneNumberInput
                value={userInfo.phone}
                onChange={(phone) => {
                  setUserInfo((prev) => ({ ...prev, phone }));
                  setPhoneError('');
                }}
                error={phoneError}
                disabled={isSubmitting}
              />

              <label className="label cursor-pointer justify-start gap-3 py-0">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary checkbox-sm"
                  checked={userInfo.whatsappConsent}
                  onChange={(e) =>
                    setUserInfo((prev) => ({ ...prev, whatsappConsent: e.target.checked }))
                  }
                  disabled={isSubmitting}
                />
                <span className="label-text text-sm">Notify me via WhatsApp</span>
              </label>

              {error && (
                <div className="alert alert-error">
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleNextFromUserInfo}
                  disabled={!isUserInfoValid() || isSubmitting}
                  className="btn btn-primary gap-2"
                >
                  {isSubmitting ? (
                    <span className="loading loading-spinner" />
                  ) : (
                    <>
                      Next
                      <FaArrowRight />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Connectors */}
          {currentStep === 'connectors' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <HiDatabase className="text-primary" />
                Connect your data sources
              </h2>

              {/* Shopify Connection */}
              <div className="card bg-base-100">
                <div className="card-body">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <FaShopify className="text-2xl text-green-600" />
                      <div>
                        <h3 className="font-semibold">Shopify Store</h3>
                        <p className="text-sm text-base-content/60">Required</p>
                      </div>
                    </div>
                    {shopifyConnected && (
                      <div className="badge badge-success gap-1">
                        <FaCheck className="text-xs" />
                        Connected
                      </div>
                    )}
                  </div>

                  {!shopifyConnected ? (
                    <div className="space-y-4">
                      <p className="text-sm text-base-content/70">
                        Connect your Shopify store to analyze your sales funnel.
                      </p>
                      {shopifyError && (
                        <div className="alert alert-error">
                          <span>{shopifyError}</span>
                        </div>
                      )}
                      <button
                        onClick={handleShopifyOAuth}
                        disabled={isConnectingShopify}
                        className="btn btn-success w-full gap-2"
                      >
                        {isConnectingShopify ? (
                          <span className="loading loading-spinner" />
                        ) : (
                          <>
                            <FaShopify />
                            Connect Shopify
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                      <FaCheck className="text-success" />
                      Shopify store connected
                    </div>
                  )}
                </div>
              </div>

              {/* Meta Ads Connection */}
              <div className="card bg-base-100">
                <div className="card-body">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <SiMeta className="text-2xl text-blue-600" />
                      <div>
                        <h3 className="font-semibold">Meta Ads</h3>
                        <p className="text-sm text-base-content/60">Optional</p>
                      </div>
                    </div>
                    {metaConnected && (
                      <div className="badge badge-success gap-1">
                        <FaCheck className="text-xs" />
                        Connected
                      </div>
                    )}
                  </div>

                  {!metaConnected ? (
                    <div className="space-y-4">
                      <p className="text-sm text-base-content/70">
                        Connect your Meta (Facebook/Instagram) Ads account to get ad performance
                        insights alongside your Shopify data.
                      </p>
                      <button
                        onClick={handleMetaOAuthStart}
                        className="btn btn-outline btn-primary w-full gap-2"
                      >
                        <SiMeta />
                        Connect Meta Ads
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-base-content/70">
                      Meta Ads connected successfully
                    </div>
                  )}
                </div>
              </div>

              {/* Meta Ads OAuth Modal */}
              <MetaAdsConnectorModal
                open={showMetaModal}
                onOpenChange={setShowMetaModal}
              />

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep('user-info')}
                  className="btn btn-ghost gap-2"
                >
                  <FaArrowLeft />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep('review')}
                  disabled={!shopifyConnected}
                  className="btn btn-primary gap-2"
                >
                  Next
                  <FaArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <HiClipboardCheck className="text-primary" />
                Review & Generate Report
              </h2>

              <div className="space-y-4">
                {/* User Info Summary */}
                <div className="card bg-base-100">
                  <div className="card-body py-4">
                    <h3 className="font-medium text-base-content/70">Your Information</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-base-content/60">Name:</span>{' '}
                        {userInfo.firstName} {userInfo.lastName}
                      </div>
                      <div>
                        <span className="text-base-content/60">Category:</span>{' '}
                        {userInfo.storeCategory}
                      </div>
                      <div>
                        <span className="text-base-content/60">Phone:</span> {userInfo.phone}
                      </div>
                      <div>
                        <span className="text-base-content/60">WhatsApp:</span>{' '}
                        {userInfo.whatsappConsent ? 'Yes' : 'No'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connections Summary */}
                <div className="card bg-base-100">
                  <div className="card-body py-4">
                    <h3 className="font-medium text-base-content/70">Connected Data Sources</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <FaShopify className="text-green-600" />
                        <span>
                          {existingShopifyConnection?.name || 'Shopify Store'}
                        </span>
                      </div>
                      {metaConnected && (
                        <div className="flex items-center gap-2">
                          <SiMeta className="text-blue-600" />
                          <span>{existingMetaConnection?.name || 'Meta Ads'}</span>
                          <span className="badge badge-sm badge-ghost">Connected</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* What happens next */}
                <div className="alert">
                  <div>
                    <h4 className="font-medium">What happens next?</h4>
                    <p className="text-sm text-base-content/70">
                      We'll sync your store data and generate insights about your sales funnel.
                      This can take a few hours depending on your store size. We'll email you
                      once your report is ready.
                    </p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="alert alert-error">
                  <span>{error}</span>
                </div>
              )}

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep('connectors')}
                  disabled={isSubmitting}
                  className="btn btn-ghost gap-2"
                >
                  <FaArrowLeft />
                  Back
                </button>
                <button
                  onClick={handleGenerateReport}
                  disabled={isSubmitting}
                  className="btn btn-primary gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner" />
                      Generating...
                    </>
                  ) : (
                    'Generate Report'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
